import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_ALL_EXAM_PAPERS } from "utils/graphql/services/ai-evaluation/queries";
import autoGradeClient from "utils/graphql/autoGradeClient";

import {
  getCollegeListData,
  getAcademicYearListData,
  getBatchListData,
  getClassListData,
  getBoardListData,
} from "utils/metadata-split/metaDataApiFetch";

interface FilterInputsProps {
  examPaperId: string;
  setExamPaperId: (value: string) => void;
  collegeId: string;
  setCollegeId: (value: string) => void;
  academicYearId: string;
  setAcademicYearId: (value: string) => void;
  batchId: string;
  setBatchId: (value: string) => void;
  classId: string;
  setClassId: (value: string) => void;
  boardId: string;
  setBoardId: (value: string) => void;
  createdFrom: string;
  setCreatedFrom: (value: string) => void;
  createdTill: string;
  setCreatedTill: (value: string) => void;
  updatedFrom: string;
  setUpdatedFrom: (value: string) => void;
  updatedTill: string;
  setUpdatedTill: (value: string) => void;
  handleApplyFilter: () => void;
  handleClearFilter: () => void;
}

const FilterInputs: React.FC<FilterInputsProps> = ({
  examPaperId,
  setExamPaperId,
  collegeId,
  setCollegeId,
  academicYearId,
  setAcademicYearId,
  batchId,
  setBatchId,
  classId,
  setClassId,
  boardId,
  setBoardId,
  createdFrom,
  setCreatedFrom,
  createdTill,
  setCreatedTill,
  updatedFrom,
  setUpdatedFrom,
  updatedTill,
  setUpdatedTill,
  handleApplyFilter,
  handleClearFilter,
}) => {
  const [colleges, setColleges] = useState<any[]>([]);
  const [academicYears, setAcademicYears] = useState<any[]>([]);
  const [batches, setBatches] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [boards, setBoards] = useState<any[]>([]);
  const [examPaperSearch, setExamPaperSearch] = useState("");
  const [selectedExamPaper, setSelectedExamPaper] = useState<any>(null);
  const [loading, setLoading] = useState<Record<string, boolean>>({
    colleges: false,
    years: false,
    batches: false,
    classes: false,
    boards: false,
  });

  const { data: examPapersData, loading: examPapersLoading } = useQuery(
    GET_ALL_EXAM_PAPERS,
    {
      variables: {
        input: {
          limit: 50,
          offset: 0,
          title: examPaperSearch,
        },
      },
      client: autoGradeClient,
      skip: false,
    }
  );

  const examPapers = examPapersData?.getExamPapers?.examPapers || [];
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading((p) => ({ ...p, colleges: true }));
        const [collegesRes]: any = await getCollegeListData(() => {});
        setColleges(collegesRes || []);
      } finally {
        setLoading((p) => ({ ...p, colleges: false }));
      }

      try {
        setLoading((p) => ({ ...p, years: true }));
        const [yearsRes]: any = await getAcademicYearListData(() => {});
        setAcademicYears(yearsRes || []);
      } finally {
        setLoading((p) => ({ ...p, years: false }));
      }

      try {
        setLoading((p) => ({ ...p, batches: true }));
        const [batchesRes]: any = await getBatchListData(() => {});
        setBatches(batchesRes || []);
      } finally {
        setLoading((p) => ({ ...p, batches: false }));
      }

      try {
        setLoading((p) => ({ ...p, classes: true }));
        const [classesRes]: any = await getClassListData(() => {});
        setClasses(classesRes || []);
      } finally {
        setLoading((p) => ({ ...p, classes: false }));
      }

      try {
        setLoading((p) => ({ ...p, boards: true }));
        const [boardsRes]: any = await getBoardListData(() => {});
        setBoards(boardsRes || []);
      } finally {
        setLoading((p) => ({ ...p, boards: false }));
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (examPaperId && examPapers.length > 0) {
      const paper = examPapers.find((p: any) => p.id === parseInt(examPaperId));
      setSelectedExamPaper(paper || null);
    } else if (!examPaperId) {
      setSelectedExamPaper(null);
    }
  }, [examPaperId, examPapers]);

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flex={1} overflow="auto" p={2}>
        <Typography variant="h6" gutterBottom>
          Filter Exam Paper Delivery
        </Typography>
        <Autocomplete
          fullWidth
          options={examPapers}
          getOptionLabel={(option: any) => option.title || ""}
          value={selectedExamPaper}
          onChange={(event, newValue) => {
            setSelectedExamPaper(newValue);
            setExamPaperId(newValue ? newValue.id.toString() : "");
          }}
          onInputChange={(event, newInputValue) => {
            setExamPaperSearch(newInputValue);
          }}
          loading={examPapersLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Exam Paper"
              variant="outlined"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {examPapersLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box>
                <Typography variant="body1">{option.title}</Typography>
                <Typography variant="caption" color="textSecondary">
                  ID: {option.id} | Created:{" "}
                  {new Date(option.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          )}
          noOptionsText="No exam papers found"
        />

        <TextField
          select
          fullWidth
          label="College"
          variant="outlined"
          margin="normal"
          value={collegeId}
          onChange={(e) => setCollegeId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {colleges.map((s: any) => (
            <MenuItem key={s.college_id} value={s.college_id}>
              {s.college_name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          label="Academic Year"
          variant="outlined"
          margin="normal"
          value={academicYearId}
          onChange={(e) => setAcademicYearId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {academicYears.map((s: any) => (
            <MenuItem key={s.year_id} value={s.year_id}>
              {s.year_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          label="Batch"
          variant="outlined"
          margin="normal"
          value={batchId}
          onChange={(e) => setBatchId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {batches.map((s: any) => (
            <MenuItem key={s.batch_id} value={s.batch_id}>
              {s.batch_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          label="Class"
          variant="outlined"
          margin="normal"
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {classes.map((s: any) => (
            <MenuItem key={s.class_id} value={s.class_id}>
              {s.class_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          label="Board"
          variant="outlined"
          margin="normal"
          value={boardId}
          onChange={(e) => setBoardId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {boards.map((s: any) => (
            <MenuItem key={s.board_id} value={s.board_id}>
              {s.board_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          label="Created From"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={createdFrom}
          onChange={(e) => setCreatedFrom(e.target.value)}
        />
        <TextField
          fullWidth
          label="Created Till"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={createdTill}
          onChange={(e) => setCreatedTill(e.target.value)}
        />
        <TextField
          fullWidth
          label="Updated From"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={updatedFrom}
          onChange={(e) => setUpdatedFrom(e.target.value)}
        />
        <TextField
          fullWidth
          label="Updated Till"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={updatedTill}
          onChange={(e) => setUpdatedTill(e.target.value)}
        />
      </Box>
      <Box
        position="sticky"
        bottom={0}
        p={2}
        borderTop="1px solid #ddd"
        bgcolor="white"
        zIndex={1}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleApplyFilter}
          sx={{ mb: 1 }}
        >
          Apply Filters
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={handleClearFilter}
        >
          Clear Filters
        </Button>
      </Box>
    </Box>
  );
};

export default FilterInputs;
